import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { UniversignValidationCoordonneesRequestSource } from '@/api/placementApi.schemas';
import { EMAIL_CHECK_MESSAGE_KEY, PHONE_CHECK_MESSAGE_KEY } from '@/constants/universignErrors';
import { COORDONNEES_CHECK_CHANNEL, COORDONNEES_CHECK_STATUS } from '@/enums/coordonneesCheck';
import { getFaireVersementJustificatifsCoordonnees } from '@/services/getFaireVersementJustificatifsCoordonnees/getFaireVersementJustificatifsCoordonnees';
import { log } from '@/utils/functions/logger/logger';

interface CoordonneesCheckPayload {
  idSouscription: number;
  email: string;
  telephone: string;
  source: NonNullable<UniversignValidationCoordonneesRequestSource>;
}

interface CoordonneesCheckResponse {
  valide?: boolean | null;
  emailToCheck?: boolean | null;
}

interface CoordonneesCheckResult {
  channel: COORDONNEES_CHECK_CHANNEL;
  status: COORDONNEES_CHECK_STATUS;
  /** Clé i18n du message affiché sous la carte ; absente quand il n'y a rien à afficher en ligne. */
  messageKey?: string;
}

// Seul le code HTTP sépare ces deux refus : `httpStatusCode` n'est pas sérialisé
// et les corps du 401 et du 404 ont la même forme.
const ERROR_STATUS_BY_HTTP_CODE: Record<number, COORDONNEES_CHECK_STATUS> = {
  401: COORDONNEES_CHECK_STATUS.MISSING_CONTACT_DETAILS,
  404: COORDONNEES_CHECK_STATUS.SEND_FAILED,
};

// SEL renvoie plusieurs 404 distincts sur le même endpoint : seul le `message`
// du corps sépare l'échec d'envoi du code de l'échec de vérification.
const SEL_CODE_SEND_FAILURE_MESSAGE = 'Envoi token temp ko';

const getCoordonneesCheckChannel = (payload: CoordonneesCheckPayload): COORDONNEES_CHECK_CHANNEL =>
  payload.email === '' ? COORDONNEES_CHECK_CHANNEL.TELEPHONE : COORDONNEES_CHECK_CHANNEL.EMAIL;

const getCoordonneesCheckSuccessStatus = (
  data: CoordonneesCheckResponse,
  channel: COORDONNEES_CHECK_CHANNEL
): COORDONNEES_CHECK_STATUS => {
  if (data.valide !== true) {
    return COORDONNEES_CHECK_STATUS.ALREADY_USED;
  }

  // `emailToCheck: true` signifie que SEL vient d'envoyer le code. Un email accepté
  // sans code envoyé ne laisse rien à saisir : il bascule sur le repli conseiller.
  // SEL renvoie toujours les deux ensemble.
  if (channel === COORDONNEES_CHECK_CHANNEL.EMAIL && data.emailToCheck !== true) {
    return COORDONNEES_CHECK_STATUS.ERROR;
  }

  return COORDONNEES_CHECK_STATUS.VALID;
};

const getCoordonneesCheckErrorStatus = (error: unknown): COORDONNEES_CHECK_STATUS => {
  if (!isAxiosError(error) || error.response === undefined) {
    return COORDONNEES_CHECK_STATUS.ERROR;
  }

  const { status } = error.response;
  const data = error.response.data as { message?: unknown } | undefined;

  if (status === 404 && data?.message === SEL_CODE_SEND_FAILURE_MESSAGE) {
    return COORDONNEES_CHECK_STATUS.CODE_SEND_FAILED;
  }

  return ERROR_STATUS_BY_HTTP_CODE[status] ?? COORDONNEES_CHECK_STATUS.ERROR;
};

const getCoordonneesCheckMessageKey = (
  status: COORDONNEES_CHECK_STATUS,
  channel: COORDONNEES_CHECK_CHANNEL
): string | undefined => {
  if (status === COORDONNEES_CHECK_STATUS.VALID) {
    return undefined;
  }

  return channel === COORDONNEES_CHECK_CHANNEL.TELEPHONE
    ? PHONE_CHECK_MESSAGE_KEY[status]
    : EMAIL_CHECK_MESSAGE_KEY[status];
};

const useFaireVersementJustificatifsCoordonnees = () => {
  const mutation = useMutation({
    mutationFn: getFaireVersementJustificatifsCoordonnees,

    onError: (error) => {
      log.error(`useFaireVersementJustificatifsCoordonnees failed: ${error}`);
    },
  });

  // Ne rejette jamais : chaque issue, y compris réseau, est traduite en statut.
  const checkCoordonnees = async (payload: CoordonneesCheckPayload): Promise<CoordonneesCheckResult> => {
    const channel = getCoordonneesCheckChannel(payload);
    let status: COORDONNEES_CHECK_STATUS;

    try {
      status = getCoordonneesCheckSuccessStatus(await mutation.mutateAsync(payload), channel);
    } catch (error) {
      status = getCoordonneesCheckErrorStatus(error);
    }

    return { channel, status, messageKey: getCoordonneesCheckMessageKey(status, channel) };
  };

  return { ...mutation, checkCoordonnees };
};

export {
  getCoordonneesCheckChannel,
  getCoordonneesCheckErrorStatus,
  getCoordonneesCheckMessageKey,
  getCoordonneesCheckSuccessStatus,
  useFaireVersementJustificatifsCoordonnees,
};
export type { CoordonneesCheckResult };
